import { getDataCorrectionRoleLabel } from "./bondHelpers";
import { buildPersonLegalName } from "../../../globals/utils/nameUtils";

const EMPTY_PRINCIPAL = {
  fid_id: null,
  name: "",
  citizenship: "",
  custom_citizenship: "",
  country: "",
  custom_country: "",
  relationship: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  occupation: "",
  ssn: "",
  is_professional_guardian: false,
  professional_guardian: null,
  is_attorney: false,
  attorney: null,
};

const CORRECTION_FORM_AMOUNT_FILE_DEFAULT = { amountEndorsementFile: null };

const EMPTY_TRUSTEE = {
  trustee_id: null,
  name: "",
  citizenship: "",
  custom_citizenship: "",
  relationship: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  occupation: "",
  ssn: "",
  is_professional_guardian: false,
  professional_guardian: null,
  is_attorney: false,
  is_attorney_home: false,
  attorney: null,
};

const EMPTY_PROBATE_PR = {
  pr_id: null,
  name: "",
  citizenship: "",
  custom_citizenship: "",
  country: "",
  custom_country: "",
  relationship: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  occupation: "",
  ssn: "",
  is_professional_guardian: false,
  professional_guardian: null,
  is_attorney: false,
  is_attorney_home: false,
  attorney: null,
};

function pushPerPrincipalNameChanges(
  changes,
  fieldLabel,
  oldList,
  newList,
  idField,
  nameField
) {
  const oldRows = oldList || [];
  const newRows = newList || [];
  const maxLen = Math.max(oldRows.length, newRows.length);
  for (let i = 0; i < maxLen; i += 1) {
    const oldName = (oldRows[i]?.[nameField] || oldRows[i]?.name || "").trim();
    const newName = (newRows[i]?.[nameField] || newRows[i]?.name || "").trim();
    const principalId = newRows[i]?.[idField] ?? oldRows[i]?.[idField];
    if (oldName !== newName && principalId != null) {
      changes.push({
        field: fieldLabel,
        principalId,
        oldValue: oldName,
        newValue: newName,
      });
    }
  }
}

function pushCommonFieldChanges(changes, form, currentData) {
  if ((form.county || "") !== (currentData?.county || "")) {
    changes.push({ field: "County", oldValue: currentData?.county || "", newValue: form.county || "" });
  }
  if ((form.courtCaseNumber || "") !== (currentData?.court_case_number || "")) {
    changes.push({
      field: "Court Case Number",
      oldValue: currentData?.court_case_number || "",
      newValue: form.courtCaseNumber || "",
    });
  }
  if (Number(form.bondAmount || 0) !== Number(currentData?.bond_amount || 0)) {
    changes.push({
      field: "Bond Amount",
      oldValue: currentData?.bond_amount ?? "",
      newValue: form.bondAmount ?? "",
    });
  }
  if (Number(form.premium || 0) !== Number(currentData?.premium || 0)) {
    changes.push({
      field: "Premium",
      oldValue: currentData?.premium ?? "",
      newValue: form.premium ?? "",
    });
  }
}

function appendNotesWithReason(existingNotes, reason) {
  return `${existingNotes || ""}${existingNotes ? "\n\n" : ""}Reason for Change: ${reason}`;
}

export const bondRetrievalDataCorrectionBuilders = {
  va: {
    getBondUpdateRoleLabel(role) {
      const ROLE_NAME_MAP = {
        super_user: "SystemAdmin",
        internal_user: "System Admin",
        law_firm_admin: "Law Firm Admin",
        law_firm_user: "Law Firm User",
      };
      return ROLE_NAME_MAP[role] || "";
    },
    getFormDefaults(data) {
      return {
        county: data?.county || "",
        courtCaseNumber: data?.court_case_number || "",
        bondAmount: data?.bond_amount || "",
        premium: data?.premium || "",
        reason: "",
        ...CORRECTION_FORM_AMOUNT_FILE_DEFAULT,
        beneficiary: {
          firstName: data?.vabond?.beneficiary_first_name || "",
          middleName: data?.vabond?.beneficiary_middle_name || "",
          lastName: data?.vabond?.beneficiary_last_name || "",
          suffix: data?.vabond?.beneficiary_suffix || "",
          aliases: data?.vabond?.beneficiary_alias || "",
        },
        principals: data?.principals?.length > 0 ? data.principals : [{ ...EMPTY_PRINCIPAL }],
      };
    },
    buildPayload(form, currentData) {
      return {
        county: form.county,
        court_case_number: form.courtCaseNumber,
        bond_amount: form.bondAmount,
        premium: form.premium,
        principals: form.principals,
        vabond: {
          beneficiary_first_name: form.beneficiary.firstName,
          beneficiary_middle_name: form.beneficiary.middleName,
          beneficiary_last_name: form.beneficiary.lastName,
          beneficiary_suffix: form.beneficiary.suffix,
          beneficiary_legal_name: buildPersonLegalName(
            form.beneficiary.firstName,
            form.beneficiary.middleName,
            form.beneficiary.lastName,
            form.beneficiary.suffix
          ),
          beneficiary_alias: form.beneficiary.aliases,
          notes: appendNotesWithReason(currentData.vabond?.notes, form.reason),
        },
      };
    },
    buildChanges(form, currentData) {
      const changes = [];
      pushCommonFieldChanges(changes, form, currentData);
      const beneficiaryFields = [
        ["firstName", "Beneficiary First Name", "beneficiary_first_name"],
        ["middleName", "Beneficiary Middle Name", "beneficiary_middle_name"],
        ["lastName", "Beneficiary Last Name", "beneficiary_last_name"],
        ["suffix", "Beneficiary Suffix", "beneficiary_suffix"],
        ["aliases", "Beneficiary Aliases", "beneficiary_alias"],
      ];
      beneficiaryFields.forEach(([formKey, label, dataKey]) => {
        const oldVal = currentData?.vabond?.[dataKey] || "";
        const newVal = form.beneficiary?.[formKey] || "";
        if (oldVal !== newVal) {
          changes.push({ field: label, oldValue: oldVal, newValue: newVal });
        }
      });
      pushPerPrincipalNameChanges(
        changes,
        "Principals",
        currentData?.principals,
        form?.principals,
        "fid_id",
        "name"
      );
      return changes;
    },
    mergeGlobalState(prevState, response, obligeeIdFromApi) {
      return {
        ...prevState,
        basicInformation: {
          ...prevState.basicInformation,
          county: response.county,
          courtCaseNumber: response.court_case_number,
          bondAmount: response.bond_amount,
          premiumAmount: response.premium,
          firstName: response.vabond?.beneficiary_first_name || "",
          middleName: response.vabond?.beneficiary_middle_name || "",
          lastName: response.vabond?.beneficiary_last_name || "",
          suffix: response.vabond?.beneficiary_suffix || "",
          aliases: response.vabond?.beneficiary_alias || "",
        },
        guardianDetails: {
          ...prevState.guardianDetails,
          guardians: response.principals,
        },
        documentUpload: {
          ...prevState.documentUpload,
          note: response.vabond?.notes || "",
        },
        obligeeInformation:
          obligeeIdFromApi !== undefined ? { obligeeId: obligeeIdFromApi } : prevState.obligeeInformation,
      };
    },
    buildEmailSummaryHtml(response) {
      return `<p><strong>Beneficiary:</strong> ${[
        response?.vabond?.beneficiary_first_name,
        response?.vabond?.beneficiary_middle_name,
        response?.vabond?.beneficiary_last_name,
        response?.vabond?.beneficiary_suffix,
      ]
        .filter(Boolean)
        .join(" ")}</p>
<p><strong>Principals:</strong> ${(response?.principals || [])
        .map((p) => p?.name)
        .filter(Boolean)
        .join(", ")}</p>`;
    },
  },
  trustee: {
    getBondUpdateRoleLabel(role) {
      const ROLE_NAME_MAP = {
        super_user: "System Admin",
        internal_user: "Internal User",
        law_firm_admin: "Law Firm Admin",
        law_firm_user: "Law Firm User",
      };
      return ROLE_NAME_MAP[role] || "";
    },
    getFormDefaults(data) {
      return {
        county: data?.county || "",
        courtCaseNumber: data?.court_case_number || "",
        bondAmount: data?.bond_amount || "",
        premium: data?.premium || "",
        reason: "",
        ...CORRECTION_FORM_AMOUNT_FILE_DEFAULT,
        legalName: data?.trustee_bond?.legal_name || "",
        trustName: data?.trustee_bond?.trust_name || "",
        aliases: data?.trustee_bond?.alias_trustee_legal_name || "",
        trustees: data?.trustees?.length > 0 ? data.trustees : [{ ...EMPTY_TRUSTEE }],
      };
    },
    buildPayload(form, currentData) {
      return {
        county: form.county,
        court_case_number: form.courtCaseNumber,
        bond_amount: form.bondAmount,
        premium: form.premium,
        trustees: form.trustees,
        trustee_bond: {
          legal_name: form.legalName,
          trust_name: form.trustName,
          alias_trustee_legal_name: form.aliases,
          notes: appendNotesWithReason(currentData.trustee_bond?.notes, form.reason),
        },
      };
    },
    buildChanges(form, currentData) {
      const changes = [];
      pushCommonFieldChanges(changes, form, currentData);
      if ((form.legalName || "") !== (currentData?.trustee_bond?.legal_name || "")) {
        changes.push({
          field: "Trust Legal Name",
          oldValue: currentData?.trustee_bond?.legal_name || "",
          newValue: form.legalName || "",
        });
      }
      if ((form.trustName || "") !== (currentData?.trustee_bond?.trust_name || "")) {
        changes.push({
          field: "Trust Name",
          oldValue: currentData?.trustee_bond?.trust_name || "",
          newValue: form.trustName || "",
        });
      }
      if ((form.aliases || "") !== (currentData?.trustee_bond?.alias_trustee_legal_name || "")) {
        changes.push({
          field: "Trustee Aliases",
          oldValue: currentData?.trustee_bond?.alias_trustee_legal_name || "",
          newValue: form.aliases || "",
        });
      }
      const oldTrustees = currentData?.trustees || [];
      const newTrustees = form?.trustees || [];
      const maxLen = Math.max(oldTrustees.length, newTrustees.length);
      for (let i = 0; i < maxLen; i += 1) {
        const oldName = (oldTrustees[i]?.name || "").trim();
        const newName = (newTrustees[i]?.name || "").trim();
        const trusteeId =
          newTrustees[i]?.trustee_id ?? oldTrustees[i]?.trustee_id;
        if (oldName !== newName && trusteeId != null) {
          changes.push({
            field: "Trustees",
            principalId: trusteeId,
            trusteeId,
            oldValue: oldName,
            newValue: newName,
          });
        }
      }
      return changes;
    },
    mergeGlobalState(prevState, response, obligeeIdFromApi) {
      return {
        ...prevState,
        basicInformation: {
          ...prevState.basicInformation,
          county: response.county,
          courtCaseNumber: response.court_case_number,
          bondAmount: response.bond_amount,
          premiumAmount: response.premium,
          legalName: response.trustee_bond?.legal_name || "",
          trustName: response.trustee_bond?.trust_name || "",
          aliases: response.trustee_bond?.alias_trustee_legal_name || "",
        },
        guardianDetails: {
          ...prevState.guardianDetails,
          guardians: response.trustees,
        },
        documentUpload: {
          ...prevState.documentUpload,
          note: response.trustee_bond?.notes || "",
        },
        obligeeInformation:
          obligeeIdFromApi !== undefined ? { obligeeId: obligeeIdFromApi } : prevState.obligeeInformation,
      };
    },
    buildEmailSummaryHtml(response) {
      return `<p><strong>Trust Legal Name:</strong> ${response?.trustee_bond?.legal_name || ""}</p>
<p><strong>Trust Name:</strong> ${response?.trustee_bond?.trust_name || ""}</p>
<p><strong>Trustees:</strong> ${(response?.trustees || [])
        .map((t) => t?.name)
        .filter(Boolean)
        .join(", ")}</p>`;
    },
  },
  guardianship: {
    getBondUpdateRoleLabel(role) {
      if (role === "super_user") return "System Admin";
      if (role === "internal_user") return "Internal User";
      if (role === "law_firm_admin") return "Law Firm Admin";
      if (role === "law_firm_user") return "Law Firm User";
      if (role === "professional_guardian_user" || role === "professional guardian") {
        return "Professional Guardian User";
      }
      if (role === "pg_contact") return "PG Contact";
      return "";
    },
    getFormDefaults(data) {
      return {
        county: data?.county || "",
        courtCaseNumber: data?.court_case_number || "",
        bondAmount: data?.bond_amount || "",
        premium: data?.premium || "",
        reason: "",
        ...CORRECTION_FORM_AMOUNT_FILE_DEFAULT,
        ward: {
          firstName: data?.guardianshipbond?.ward_first_name || "",
          middleInitial: data?.guardianshipbond?.ward_middle_name || "",
          lastName: data?.guardianshipbond?.ward_last_name || "",
          aliases: data?.guardianshipbond?.ward_alias || "",
        },
        guardians:
          data?.guardians?.length > 0
            ? data.guardians
            : [{ guardian_id: null, guardian_name: "" }],
      };
    },
    buildPayload(form, currentData) {
      return {
        county: form.county,
        court_case_number: form.courtCaseNumber,
        bond_amount: form.bondAmount,
        premium: form.premium,
        guardians: form.guardians,
        guardianshipbond: {
          ward_first_name: form.ward.firstName,
          ward_middle_name: form.ward.middleInitial,
          ward_last_name: form.ward.lastName,
          ward_legal_name: buildPersonLegalName(
            form.ward.firstName,
            form.ward.middleInitial,
            form.ward.lastName
          ),
          alias_ward_legal_name: form.ward.aliases,
          notes: appendNotesWithReason(currentData.guardianshipbond?.notes, form.reason),
        },
      };
    },
    buildChanges(form, currentData) {
      const changes = [];
      pushCommonFieldChanges(changes, form, currentData);
      if ((form.ward.firstName || "") !== (currentData?.guardianshipbond?.ward_first_name || "")) {
        changes.push({
          field: "Ward First Name",
          oldValue: currentData?.guardianshipbond?.ward_first_name || "",
          newValue: form.ward.firstName || "",
        });
      }
      if ((form.ward.middleInitial || "") !== (currentData?.guardianshipbond?.ward_middle_name || "")) {
        changes.push({
          field: "Ward Middle Name",
          oldValue: currentData?.guardianshipbond?.ward_middle_name || "",
          newValue: form.ward.middleInitial || "",
        });
      }
      if ((form.ward.lastName || "") !== (currentData?.guardianshipbond?.ward_last_name || "")) {
        changes.push({
          field: "Ward Last Name",
          oldValue: currentData?.guardianshipbond?.ward_last_name || "",
          newValue: form.ward.lastName || "",
        });
      }
      if ((form.ward.aliases || "") !== (currentData?.guardianshipbond?.ward_alias || "")) {
        changes.push({
          field: "Ward Aliases",
          oldValue: currentData?.guardianshipbond?.ward_alias || "",
          newValue: form.ward.aliases || "",
        });
      }
      pushPerPrincipalNameChanges(
        changes,
        "Guardians",
        currentData?.guardians,
        form?.guardians,
        "guardian_id",
        "guardian_name"
      );
      return changes;
    },
    mergeGlobalState(prevState, response, obligeeIdFromApi) {
      return {
        ...prevState,
        basicInformation: {
          ...prevState.basicInformation,
          county: response.county,
          courtCaseNumber: response.court_case_number,
          bondAmount: response.bond_amount,
          premiumAmount: response.premium,
          firstName: response.guardianshipbond?.ward_first_name,
          middleName: response.guardianshipbond?.ward_middle_name,
          lastName: response.guardianshipbond?.ward_last_name,
          suffix: response.guardianshipbond?.ward_suffix,
          aliases: response.guardianshipbond?.ward_alias,
          dateOfDeath: response.guardianshipbond?.ward_date_of_birth,
        },
        guardianDetails: {
          ...prevState.guardianDetails,
          guardians: response.guardians,
        },
        documentUpload: {
          ...prevState.documentUpload,
          note: response.guardianshipbond?.notes || "",
        },
        obligeeInformation:
          obligeeIdFromApi !== undefined ? { obligeeId: obligeeIdFromApi } : prevState.obligeeInformation,
      };
    },
    buildEmailSummaryHtml(response) {
      return `<p><strong>Ward:</strong> ${[
        response?.guardianshipbond?.ward_first_name,
        response?.guardianshipbond?.ward_middle_name,
        response?.guardianshipbond?.ward_last_name,
      ]
        .filter(Boolean)
        .join(" ")}</p>
<p><strong>Guardians:</strong> ${(response?.guardians || [])
        .map((g) => g?.guardian_name || g?.name)
        .filter(Boolean)
        .join(", ")}</p>`;
    },
  },
  probate: {
    getBondUpdateRoleLabel(role) {
      if (role === "super_user") return "System Admin";
      if (role === "internal_user") return "Internal User";
      if (role === "law_firm_admin") return "Law Firm Admin";
      if (role === "law_firm_user") return "Law Firm User";
      return "";
    },
    getFormDefaults(data) {
      return {
        county: data?.county || "",
        courtCaseNumber: data?.court_case_number || "",
        bondAmount: data?.bond_amount || "",
        premium: data?.premium || "",
        reason: "",
        ...CORRECTION_FORM_AMOUNT_FILE_DEFAULT,
        deceased: {
          firstName: data?.probatebond?.deceased_legal_first_name || "",
          middleInitial: data?.probatebond?.deceased_legal_middle_name || "",
          lastName: data?.probatebond?.deceased_legal_last_name || "",
          aliases: data?.probatebond?.alias_deceased_legal_name || "",
        },
        principals:
          data?.personal_rep?.length > 0 ? data.personal_rep : [{ ...EMPTY_PROBATE_PR }],
      };
    },
    buildPayload(form, currentData) {
      return {
        county: form.county,
        court_case_number: form.courtCaseNumber,
        bond_amount: form.bondAmount,
        premium: form.premium,
        personal_rep: form.principals,
        probatebond: {
          deceased_legal_first_name: form.deceased.firstName,
          deceased_legal_middle_name: form.deceased.middleInitial,
          deceased_legal_last_name: form.deceased.lastName,
          deceased_legal_name: buildPersonLegalName(
            form.deceased.firstName,
            form.deceased.middleInitial,
            form.deceased.lastName
          ),
          alias_deceased_legal_name: form.deceased.aliases,
          notes: appendNotesWithReason(currentData.probatebond?.notes, form.reason),
        },
      };
    },
    buildChanges(form, currentData) {
      const changes = [];
      pushCommonFieldChanges(changes, form, currentData);
      if ((form.deceased.firstName || "") !== (currentData?.probatebond?.deceased_legal_first_name || "")) {
        changes.push({
          field: "Deceased First Name",
          oldValue: currentData?.probatebond?.deceased_legal_first_name || "",
          newValue: form.deceased.firstName || "",
        });
      }
      if ((form.deceased.middleInitial || "") !== (currentData?.probatebond?.deceased_legal_middle_name || "")) {
        changes.push({
          field: "Deceased Middle Name",
          oldValue: currentData?.probatebond?.deceased_legal_middle_name || "",
          newValue: form.deceased.middleInitial || "",
        });
      }
      if ((form.deceased.lastName || "") !== (currentData?.probatebond?.deceased_legal_last_name || "")) {
        changes.push({
          field: "Deceased Last Name",
          oldValue: currentData?.probatebond?.deceased_legal_last_name || "",
          newValue: form.deceased.lastName || "",
        });
      }
      if ((form.deceased.aliases || "") !== (currentData?.probatebond?.alias_deceased_legal_name || "")) {
        changes.push({
          field: "Deceased Aliases",
          oldValue: currentData?.probatebond?.alias_deceased_legal_name || "",
          newValue: form.deceased.aliases || "",
        });
      }
      pushPerPrincipalNameChanges(
        changes,
        "Principals",
        currentData?.personal_rep,
        form?.principals,
        "pr_id",
        "name"
      );
      return changes;
    },
    mergeGlobalState(prevState, response, obligeeIdFromApi) {
      return {
        ...prevState,
        basicInformation: {
          ...prevState.basicInformation,
          county: response.county,
          courtCaseNumber: response.court_case_number,
          bondAmount: response.bond_amount,
          premiumAmount: response.premium,
          firstName: response.probatebond?.deceased_legal_first_name,
          middleName: response.probatebond?.deceased_legal_middle_name,
          lastName: response.probatebond?.deceased_legal_last_name,
          suffix: response.probatebond?.deceased_legal_suffix,
          aliases: response.probatebond?.alias_deceased_legal_name,
        },
        guardianDetails: {
          ...prevState.guardianDetails,
          guardians: response.personal_rep,
        },
        documentUpload: {
          ...prevState.documentUpload,
          note: response.probatebond?.notes || "",
        },
        obligeeInformation:
          obligeeIdFromApi !== undefined ? { obligeeId: obligeeIdFromApi } : prevState.obligeeInformation,
      };
    },
    buildEmailSummaryHtml(response) {
      return `<p><strong>Deceased:</strong> ${[
        response?.probatebond?.deceased_legal_first_name,
        response?.probatebond?.deceased_legal_middle_name,
        response?.probatebond?.deceased_legal_last_name,
      ]
        .filter(Boolean)
        .join(" ")}</p>
<p><strong>Principals:</strong> ${(response?.personal_rep || [])
        .map((p) => p?.name)
        .filter(Boolean)
        .join(", ")}</p>`;
    },
  },
};

export function getBondRetrievalCorrectionBuilder(kind) {
  const builder = bondRetrievalDataCorrectionBuilders[kind];
  if (!builder) {
    throw new Error(`Unknown bond retrieval correction kind: ${kind}`);
  }
  return builder;
}

export function buildChangesTableHtml(changes) {
  if (changes.length === 0) {
    return '<p style="font-family:Arial, sans-serif; font-size:14px; color:#6b7280;">No field-level changes detected.</p>';
  }

  return `
<table style="width:100%; border-collapse:collapse; margin-top:12px; font-family:Arial, sans-serif; font-size:14px;">
  <thead>
    <tr>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Field</th>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">Old Value</th>
      <th style="text-align:left; padding:8px 10px; border:1px solid #e5e7eb; background:#f9fafb; color:#111827;">New Value</th>
    </tr>
  </thead>
  <tbody>
    ${changes
      .map(
        (change) => `
      <tr>
        <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#374151;">${change.field}</td>
        <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#6b7280;">${(change.oldValue ?? "").toString()}</td>
        <td style="padding:8px 10px; border:1px solid #e5e7eb; color:#111827; font-weight:600;">${(change.newValue ?? "").toString()}</td>
      </tr>
    `
      )
      .join("")}
  </tbody>
</table>`;
}

export function isBondAmountCorrection(form, currentData) {
  return (
    Number(form?.bondAmount || 0) !== Number(currentData?.bond_amount || 0)
  );
}

export function shouldUseEndorsePath(changes, bondCreatedAt, principalFieldName) {
  const ENDORSABLE_FIELDS = new Set(["County", principalFieldName]);
  const isOver48h =
    bondCreatedAt && Date.now() - new Date(bondCreatedAt).getTime() > 48 * 60 * 60 * 1000;
  const hasEndorsableChange = changes.some((c) => ENDORSABLE_FIELDS.has(c.field));
  return hasEndorsableChange && isOver48h;
}

export function getCorrectionContactInfo(userdetails) {
  return {
    contactName:
      [userdetails?.first_name, userdetails?.last_name].filter(Boolean).join(" ") || "Portal User",
    contactEmail: userdetails?.email || userdetails?.username || "noreply@vermost.com",
  };
}

export function getCorrectionRoleLabel(role) {
  return getDataCorrectionRoleLabel(role);
}
